if (!customElements.get('countdown-timer')) {
  customElements.define(
    'countdown-timer',
    class CountdownTimer extends HTMLElement {
      constructor() {
        super();

        this.init();
      }

      init() {
        this.MS_IN_SEC = 1000;
        this.MS_IN_MIN = 60 * this.MS_IN_SEC;
        this.MS_IN_HRS = 60 * this.MS_IN_MIN;
        this.MS_IN_DAY = 24 * this.MS_IN_HRS;

        this.selectorMapping = {
          day: '[data-timer-day]',
          hour: '[data-timer-hour]',
          minute: '[data-timer-minute]',
          second: '[data-timer-second]',
        };
        this.units = ['day', 'hour', 'minute', 'second'];
        this.type = this.dataset.type;
        this.duration = this.dataset.duration;
        this.targetTimeResolver = null;
        this.shouldAddLeadingZero = true;

        this.initCountdown();
      }

      initCountdown() {
        switch (this.type) {
          case 'fixed_time': {
            const countdownData = this.dataset.dueDate;
            if (countdownData) {
              const fixedTimeConfig = this.getFixedTimeConfig(countdownData);
              if (fixedTimeConfig) {
                this.targetTime = fixedTimeConfig.targetTime;
                this.targetTimeResolver = fixedTimeConfig.targetTimeResolver;
              }
            }
            break;
          }
          default: // Evergreen.
            this.targetTimeResolver = (nowMs = Date.now()) => this.getEvergreenDueDate(nowMs).getTime();
            this.setNextTargetTime();
            break;
        }

        if (!this.targetTime) {
          return;
        }

        this.startTime = Date.now();
        if (this.targetTime > this.startTime) {
          this.fetchDomElements();
          this.beginCountdown();
        }
      }

      fetchDomElements() {
        this.elements = {};
        this.units.forEach((unit) => {
          this.elements[unit] = this.querySelector(this.selectorMapping[unit]);
        });
      }

      beginCountdown() {
        this.tick();
        this.intervalId = setInterval(() => {
          this.tick();
        }, 1000);
        this.classList.remove('hidden');
      }

      tick() {
        const nowMs = Date.now();
        let remainingTime = this.targetTime - nowMs;

        if (remainingTime <= 0 && this.targetTimeResolver) {
          this.setNextTargetTime(nowMs);
          remainingTime = this.targetTime - nowMs;
        }

        if (remainingTime <= 0) {
          this.terminateCountdown();
          return;
        }

        this.updateDisplay(remainingTime);
      }

      updateDisplay(remainingTime) {
        const timeComponents = this.computeTimeComponents(remainingTime);
        this.units.forEach((unit) => {
          if (this.elements[unit]) {
            this.elements[unit].textContent = this.formatTimeComponent(timeComponents[unit]);
          }
        });
      }

      computeTimeComponents(ms) {
        return {
          day: Math.floor(ms / this.MS_IN_DAY),
          hour: Math.floor((ms % this.MS_IN_DAY) / this.MS_IN_HRS),
          minute: Math.floor((ms % this.MS_IN_HRS) / this.MS_IN_MIN),
          second: Math.floor((ms % this.MS_IN_MIN) / this.MS_IN_SEC),
        };
      }

      formatTimeComponent(value) {
        return this.shouldAddLeadingZero && value < 10 ? `0${value}` : value.toString();
      }

      terminateCountdown() {
        clearInterval(this.intervalId);
        this.intervalId = null;
        if (!this.targetTimeResolver) {
          this.classList.add('hidden');
        }
      }

      resetCountdown() {
        clearInterval(this.intervalId);
        this.units.forEach((unit) => {
          if (this.elements[unit]) {
            this.elements[unit].textContent = '00';
          }
        });
      }

      setNextTargetTime(nowMs = Date.now()) {
        if (!this.targetTimeResolver) return;
        this.targetTime = this.targetTimeResolver(nowMs);
      }

      getFixedTimeConfig(dateValue) {
        const dailyTime = this.parseTimeOfDay(dateValue);
        if (dailyTime) {
          return {
            targetTime: this.getNextDailyDueDate(dailyTime).getTime(),
            targetTimeResolver: (nowMs = Date.now()) => this.getNextDailyDueDate(dailyTime, nowMs).getTime(),
          };
        }

        const absoluteDate = this.parseAbsoluteDateTime(dateValue);
        if (!absoluteDate) {
          return null;
        }

        return {
          targetTime: absoluteDate.getTime(),
          targetTimeResolver: null,
        };
      }

      getNextDailyDueDate(timeParts, nowMs = Date.now()) {
        const now = new Date(nowMs);
        const dueDate = new Date(now);
        dueDate.setHours(timeParts.hours, timeParts.minutes, 0, 0);

        if (dueDate.getTime() <= nowMs) {
          dueDate.setDate(dueDate.getDate() + 1);
        }

        return dueDate;
      }

      getEvergreenDueDate(nowMs = Date.now()) {
        let intervalHrs;
        const now = new Date(nowMs);
        const dueDate = new Date(now);

        switch (this.duration) {
          case 'every_month':
            dueDate.setMonth(dueDate.getMonth() + 1); // Next month.
            dueDate.setDate(1); // Start from the 1st day of the next month.
            dueDate.setHours(0, 0, 0, 0);
            return dueDate;
          case 'every_week':
            const dayOfWeek = now.getDay();
            const daysUntilNextWeek = 7 - dayOfWeek;
            dueDate.setDate(now.getDate() + daysUntilNextWeek); // Move to next Sunday.
            dueDate.setHours(0, 0, 0, 0); // Set the time to 00:00:00.
            return dueDate;
          case 'every_2_hrs':
            intervalHrs = 2;
            break;
          case 'every_6_hrs':
            intervalHrs = 6;
            break;
          case 'every_12_hrs':
            intervalHrs = 12;
            break;
          case 'every_24_hrs':
            intervalHrs = 24;
            break;
        }

        // Possible start points based on the interval.
        const startPoints = [];
        for (let i = 0; i < 24; i += intervalHrs) {
          startPoints.push(i);
        }

        // Find the next start point.
        for (let i = 0; i < startPoints.length; i++) {
          const candidateDate = new Date(now);
          candidateDate.setHours(startPoints[i], 0, 0, 0);

          if (candidateDate.getTime() > nowMs) {
            return candidateDate;
          }
        }

        // If no future point today, roll over to the first point tomorrow.
        dueDate.setDate(dueDate.getDate() + 1);
        dueDate.setHours(startPoints[0], 0, 0, 0);
        return dueDate;
      }

      parseTimeOfDay(timeString) {
        if (!timeString) return null;

        const compact = timeString.toLowerCase().replace(/\s+/g, '');

        let match = compact.match(/^(\d{1,2}):(\d{2})([ap])m?$/);
        if (match) {
          return this.parseHourMinuteMeridiem(match[1], match[2], match[3]);
        }

        match = compact.match(/^(\d{1,2}):(\d{2})$/);
        if (match) {
          const hours = parseInt(match[1], 10);
          const minutes = parseInt(match[2], 10);
          if (!this.isValidTime(hours, minutes)) return null;
          return { hours, minutes };
        }

        match = compact.match(/^(\d{1,2})([ap])m?$/);
        if (match) {
          const hours = parseInt(match[1], 10);
          const minutes = 0;

          if (hours > 12) {
            if (!this.isValidTime(hours, minutes)) return null;
            return { hours, minutes };
          }

          return this.parseHourMinuteMeridiem(match[1], minutes, match[2]);
        }

        return null;
      }

      parseHourMinuteMeridiem(hourValue, minuteValue, meridiem) {
        const hoursRaw = parseInt(hourValue, 10);
        const minutes = parseInt(minuteValue, 10);
        const mer = String(meridiem).toLowerCase();

        if (!this.isValidTime(hoursRaw, minutes)) {
          if (hoursRaw > 12 && minutes >= 0 && minutes < 60) {
            return { hours: hoursRaw % 24, minutes };
          }
          return null;
        }

        if (hoursRaw > 12) {
          return { hours: hoursRaw % 24, minutes };
        }

        let hours = hoursRaw;
        if (mer === 'p') {
          if (hours !== 12) hours += 12;
        } else if (mer === 'a') {
          if (hours === 12) hours = 0;
        }

        return this.isValidTime(hours, minutes) ? { hours, minutes } : null;
      }

      parseAbsoluteDateTime(dateValue) {
        if (!dateValue) return null;

        const nativeDate = new Date(dateValue);
        if (!Number.isNaN(nativeDate.getTime())) {
          return nativeDate;
        }

        const match = String(dateValue)
          .trim()
          .match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})[,\sT]+(\d{1,2})(?::(\d{2}))?\s*([ap]m?)?$/i);

        if (!match) {
          return null;
        }

        let hours = parseInt(match[4], 10);
        const minutes = parseInt(match[5] || '0', 10);
        const meridiem = match[6] ? match[6].charAt(0).toLowerCase() : null;

        if (meridiem) {
          const parsedTime = this.parseHourMinuteMeridiem(hours, minutes, meridiem);
          if (!parsedTime) return null;
          hours = parsedTime.hours;
        } else if (!this.isValidTime(hours, minutes)) {
          return null;
        }

        const parsedDate = new Date(
          parseInt(match[1], 10),
          parseInt(match[2], 10) - 1,
          parseInt(match[3], 10),
          hours,
          minutes,
          0,
          0
        );

        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
      }

      isValidTime(hours, minutes) {
        return Number.isFinite(hours) && Number.isFinite(minutes) && hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
      }
    }
  );
}
